import React, { type RefObject, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { DailyrentBanner } from '../../components/DailyrentBanner';
import { useFindDealsBannerMountNode } from '../PostWithDealsWidgets/hooks';
import { useTrackCpdDealsShow } from '../../hooks/useTrackCpdDealsShow';
import { trackDailyrentBannerClick, trackDailyrentBannerShow } from './tracking';
import type { IDailyrentBannerConfig } from '../../types/dailyrentBannerConfig';

const DAILYRENT_BANNER_DEFAULT_URL = 'https://www.cian.ru/posutochno/';

export interface IDailyrentBannerContainerProps {
  contentRef: RefObject<HTMLDivElement>;
  /** Id контейнера, оставленного useContentDailyrentBanner на месте маркера */
  containerId: string | null;
  /** Контент статьи: при его смене контейнер ищется заново */
  content: string;
  postId: number;
  config: IDailyrentBannerConfig;
}

interface ITrackedDailyrentBannerProps {
  url: string;
  onShow(): void;
  onClick(): void;
}

/**
 * Трекает показ, когда баннер виден наполовину. Отдельный компонент нужен, чтобы
 * ref появился уже при первом рендере: useIntersectionObserver подписывается по ref.current
 */
const TrackedDailyrentBanner: React.FC<ITrackedDailyrentBannerProps> = ({ url, onShow, onClick }) => {
  const bannerRef = useTrackCpdDealsShow(onShow);

  return (
    <div ref={bannerRef}>
      <DailyrentBanner url={url} onClick={onClick} />
    </div>
  );
};

/**
 * Монтирует баннер посуточной аренды в контент статьи через портал
 * и трекает его показ и клик
 */
export const DailyrentBannerContainer: React.FC<IDailyrentBannerContainerProps> = ({
  contentRef,
  containerId,
  content,
  postId,
  config: { clickUrl },
}) => {
  const mountNode = useFindDealsBannerMountNode(contentRef, true, containerId, content);
  const url = clickUrl || DAILYRENT_BANNER_DEFAULT_URL;

  const handleShow = useCallback(() => {
    trackDailyrentBannerShow(postId);
  }, [postId]);

  const handleClick = useCallback(() => {
    trackDailyrentBannerClick(postId);
  }, [postId]);

  if (!mountNode) {
    return null;
  }

  return createPortal(<TrackedDailyrentBanner url={url} onShow={handleShow} onClick={handleClick} />, mountNode);
};
